import { fm } from "lib/localization";
import { defineMessages } from "react-intl";
import { leftPad } from "./leftPad";

export const unitMessages = defineMessages({
  degreesLong: {
    id: "unitMessages.degreesLong",
    defaultMessage: "degrees",
    description: "A measurement of steepness, expressed in degrees",
  },
  ftLong: {
    id: "unitMessages.ftLong",
    defaultMessage: "feet",
    description: "An imperial unit of distance",
  },
  ftShort: {
    id: "unitMessages.ftShort",
    defaultMessage: "ft",
    description: "The imperial unit of distance feet, abbreviated",
  },
  gradeLong: {
    id: "unitMessages.gradeLong",
    defaultMessage: "percent",
    description: "A measurement of steepness, expressed as a percentage",
  },
  hrsShort: {
    id: "unitMessages.hrsShort",
    defaultMessage: "hrs",
    description: "A measurement of time, hours, abbreviated",
  },
  kmLong: {
    id: "unitMessages.kmLong",
    defaultMessage: "kilometers",
    description: "A metric unit of distance",
  },
  kmShort: {
    id: "unitMessages.kmShort",
    defaultMessage: "km",
    description: "The metric unit of distance kilometers, abbreviated",
  },
  kphLong: {
    id: "unitMessages.kphLong",
    defaultMessage: "km per hour",
    description: "A metric measurement of velocity",
  },
  kphShort: {
    id: "unitMessages.kphShort",
    defaultMessage: "kph",
    description: "A metric measurement of velocity, kilometers per hour, abbreviated",
  },
  miLong: {
    id: "unitMessages.miLong",
    defaultMessage: "miles",
    description: "An imperial unit of distance",
  },
  miShort: {
    id: "unitMessages.miShort",
    defaultMessage: "mi",
    description: "The imperial unit of distance miles, abbreviated",
  },
  mLong: {
    id: "unitMessages.mLong",
    defaultMessage: "meters",
    description: "A metric unit of distance",
  },
  mphLong: {
    id: "unitMessages.mphLong",
    defaultMessage: "miles per hour",
    description: "An imperial measurement of velocity",
  },
  mphShort: {
    id: "unitMessages.mphShort",
    defaultMessage: "mph",
    description: "An imperial measurement of velocity, miles per hour, abbreviated",
  },
  mShort: {
    id: "unitMessages.mShort",
    defaultMessage: "m",
    description: "The metric unit of distance meters, abbreviated",
  },
  vmh: {
    id: "unitMessages.vmh",
    defaultMessage: "Vm/h",
    description:
      "The units of VAM (from the Italian term velocità ascensionale media). Stands for Vertical Meters per Hour",
  },
});

const unit = (unitSpec) => (val, stringOpts) => {
  const { intl } = stringOpts;
  const locale = intl ? intl.locale : "en-US";
  const intlLong = intl && unitSpec.longMessage ? fm(intl, unitSpec.longMessage) : unitSpec.long;
  const intlShort = intl && unitSpec.shortMessage ? fm(intl, unitSpec.shortMessage) : unitSpec.short;

  return {
    value: val,
    valueToString: (opts) =>
      val.toLocaleString(locale, {
        ...unitSpec.defaultStringOpts,
        ...stringOpts,
        ...opts,
      }),
    toString: ({ short = false, ...opts } = {}) => {
      const valueString = val.toLocaleString(locale, {
        ...unitSpec.defaultStringOpts,
        ...stringOpts,
        ...opts,
      });
      const unitString = short ? intlShort : intlLong;
      const specSeperator = unitSpec.seperator && (short ? unitSpec.seperator.short : unitSpec.seperator.long);
      const seperator = specSeperator !== undefined ? specSeperator : " "; // this is a bit wordy because '' is falsy in JS
      return valueString + seperator + unitString;
    },
    ...unitSpec,
    long: intlLong,
    short: intlShort,
  };
};

export const meters = unit({
  long: "meters",
  longMessage: unitMessages.mLong,
  short: "m",
  shortMessage: unitMessages.mShort,
  compound: false,
  defaultStringOpts: { useGrouping: true, maximumFractionDigits: 0 },
});

export const km = unit({
  long: "kilometers",
  longMessage: unitMessages.kmLong,
  short: "km",
  shortMessage: unitMessages.kmShort,
  compound: false,
  defaultStringOpts: { useGrouping: true, maximumFractionDigits: 1 },
});

export const feet = unit({
  long: "feet",
  longMessage: unitMessages.ftLong,
  short: "ft",
  shortMessage: unitMessages.ftShort,
  compound: false,
  defaultStringOpts: { useGrouping: true, maximumFractionDigits: 0 },
});

export const miles = unit({
  long: "miles",
  longMessage: unitMessages.miLong,
  short: "mi",
  shortMessage: unitMessages.miShort,
  compound: false,
  defaultStringOpts: { useGrouping: true, maximumFractionDigits: 1 },
});

export const kmPerHour = unit({
  long: "km per hour",
  longMessage: unitMessages.kphLong,
  short: "kph",
  shortMessage: unitMessages.kphShort,
  compound: true,
  defaultStringOpts: { useGrouping: true, maximumFractionDigits: 1 },
});

export const milesPerHour = unit({
  long: "miles per hour",
  longMessage: unitMessages.mphLong,
  short: "mph",
  shortMessage: unitMessages.mphShort,
  compound: true,
  defaultStringOpts: { useGrouping: true, maximumFractionDigits: 1 },
});

export const grade = unit({
  long: "percent",
  longMessage: unitMessages.gradeLong,
  short: "%",
  compound: false,
  defaultStringOpts: {
    useGrouping: false,
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  },
  seperator: { short: "", long: " " },
});

export const degrees = unit({
  long: "degrees",
  longMessage: unitMessages.degreesLong,
  short: "º",
  compound: false,
  defaultStringOpts: {
    useGrouping: false,
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  },
  seperator: { short: "", long: " " },
});

const MINUTE = 60;
const HOUR = 60 * 60;
const timePad = (num) => leftPad(num, 2, "0");

export const minuteSecond = (seconds, stringOpts) => ({
  long: "mm:ss",
  short: "mm:ss",
  compound: false,
  toString: ({ short = false } = {}) => {
    const mins = timePad(Math.floor(seconds / MINUTE));
    const secs = timePad(Math.floor(seconds) % MINUTE);
    return `${mins}:${secs}`;
  },
  valueToString: () => {
    const mins = timePad(Math.floor(seconds / MINUTE));
    const secs = timePad(Math.floor(seconds) % MINUTE);
    return `${mins}:${secs}`;
  },
});

export const hourMinute = (seconds, stringOpts) => ({
  long: "hh:mm",
  short: "hh:mm",
  compound: false,
  defaultStringOpts: "",
  valueToString: () => {
    const hrs = Math.floor(seconds / HOUR);
    const mins = timePad(Math.floor((seconds % HOUR) / MINUTE));
    return `${hrs}:${mins}`;
  },
  toString: ({ short = false } = {}) => {
    const hrs = Math.floor(seconds / HOUR);
    const mins = timePad(Math.floor((seconds % HOUR) / MINUTE));
    return `${hrs}:${mins}`;
  },
});

export const hourMinuteSecond = (seconds, stringOpts) => ({
  long: "hh:mm:ss",
  short: "hh:mm:ss",
  compound: false,
  defaultStringOpts: "",
  valueToString: () => {
    const hrs = timePad(Math.floor(seconds / HOUR));
    const mins = timePad(Math.floor((seconds % HOUR) / MINUTE));
    const secs = timePad(Math.floor(seconds) % MINUTE);
    return `${hrs}:${mins}:${secs}`;
  },
  toString: ({ short = false } = {}) => {
    const hrs = Math.floor(seconds / HOUR);
    const mins = timePad(Math.floor((seconds % HOUR) / MINUTE));
    const secs = timePad(Math.floor(seconds) % MINUTE);
    return `${hrs}:${mins}:${secs}`;
  },
});

export const paceKm = (secondsPerKm) => ({
  short: "min/km",
  long: "minutes per km",
  compound: true,
  valueToString: hourMinuteSecond(secondsPerKm).valueToString,
  toString: ({ short = false } = {}) => {
    return hourMinuteSecond(secondsPerKm).valueToString() + " " + (short ? "min/km" : "minutes per km");
  },
});

export const paceMiles = (secondsPerMile) => ({
  short: "min/mi",
  long: "minutes per mi",
  compound: true,
  valueToString: hourMinuteSecond(secondsPerMile).valueToString,
  toString: ({ short = false } = {}) => {
    return hourMinuteSecond(secondsPerMile).valueToString() + " " + (short ? "min/mi" : "minutes per mile");
  },
});
